import { ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnChanges, OnInit, Output } from '@angular/core';
import { GetSenderInfoResponse, GetSenderInfoResponseInterface, Sender, SenderDomain } from '@vendasta/email';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { EmailValidators } from '../../../../email-form-validators';
import { oneResultFrom, SubscriptionList } from '@vendasta/rx-utils';
import { SavedState, UNSAVED_DATA_HANDLER_TOKEN, UnsavedDataHandler } from '../dependencies';
import { Observable, Subscription } from 'rxjs';
import { SelectedDomain } from './selected-domain';
import { DEFAULT_SENDER_DOMAIN } from './default';
import { MatDialog } from '@angular/material/dialog';
import { DomainsDialogComponent, Result as DialogResult } from '../../domains-dialog/domains-dialog.component';
import { map } from 'rxjs/operators';
import { DomainCacheService } from '../../domain-cache.service';

export interface SaveData {
  domain: string;
  preferredDisplayName: string;
  preferredEmailUsername: string;
  senderName: string;
  replyTo: string;
}

@Component({
  selector: 'email-sender-info-form',
  templateUrl: './sender-info-form.component.html',
  styleUrls: ['./sender-info-form.component.scss'],
})
export class SenderInfoFormComponent implements OnInit, OnChanges {
  @Output() readonly domainChangeRequested = new EventEmitter<string>();
  @Output() readonly invalidDomainChangeRequested = new EventEmitter<string>();
  @Output() readonly authorizeRequested = new EventEmitter<void>();

  @Input() readonly sender: Sender;

  @Input() readonly senderInfo: GetSenderInfoResponse;

  /**
   * domainOverride sets the domain that is displayed in this component. It
   * is displayed INSTEAD OF the sender's actual domain. (e.g. prior to saving)
   */
  @Input() domainOverride: string;

  domains$: Observable<SenderDomain[]>;

  senderInfoForm: UntypedFormGroup = new UntypedFormGroup({
    senderName: new UntypedFormControl('', [Validators.required]),
    useOwn: new UntypedFormControl(''),
    senderEmailUsername: new UntypedFormControl('', [Validators.required, EmailValidators.emailUsername()]),
    replyTo: new UntypedFormControl('', [EmailValidators.fullEmailAddress()]),
  });

  selectedDomain: SelectedDomain = SelectedDomain.default();

  useOwn: boolean;

  private readonly subs = SubscriptionList.new();
  private notifier: Subscription | null = null;

  constructor(
    private readonly cdr: ChangeDetectorRef,
    @Inject(UNSAVED_DATA_HANDLER_TOKEN) private readonly unsavedHandler: UnsavedDataHandler,
    private readonly dialog: MatDialog,
    private readonly domainService: DomainCacheService,
  ) {
    this.subs.add(this.senderInfoForm.get('useOwn').valueChanges, (value) => this.handleUseOwnChanged(value));
  }

  ngOnChanges(): void {
    this.updateFormValuesIfUnset(this.senderInfo);
    if (this.domainOverride) {
      this.senderInfoForm.get('useOwn').setValue(true);
      const senderInfo: GetSenderInfoResponseInterface = {
        domain: this.domainOverride,
        isPlatformAuthorized: true,
      };
      this.selectedDomain = SelectedDomain.fromSenderInfo(senderInfo, true);
    }
  }

  ngOnInit(): void {
    this.domains$ = this.domainService.listAllDomains(this.sender.type, this.sender.id).pipe(map((r) => r.domains));
  }

  private handleUseOwnChanged(useOwnDomain: boolean): void {
    this.useOwn = useOwnDomain;
    this.selectedDomain = SelectedDomain.fromSenderInfo(this.senderInfo, useOwnDomain);
    this.cdr.detectChanges();
  }

  public handleAuthorizeDomain(): void {
    this.authorizeRequested.emit();
  }

  private updateFormValuesIfUnset(info: GetSenderInfoResponseInterface): void {
    this.notifier?.unsubscribe();
    this.senderInfoForm.get('useOwn').setValue(!info.isDefaultForSenderType);
    const senderName = this.senderInfoForm.get('senderName');
    if (!senderName.value) {
      senderName.setValue(info.preferredEmailDisplayName);
    }
    const senderEmailUsername = this.senderInfoForm.get('senderEmailUsername');
    if (!senderEmailUsername.value) {
      senderEmailUsername.setValue(info.preferredEmailUsername);
    }
    const replyTo = this.senderInfoForm.get('replyTo');
    if (!replyTo.value) {
      replyTo.setValue(info.preferredReplyToEmailAddress);
    }
    this.notifier = this.senderInfoForm.valueChanges.subscribe(() =>
      this.unsavedHandler.notifyStateChanged(SavedState.HAS_UNSAVED_CHANGES),
    );
  }

  getSaveData(): SaveData {
    // TODO: Prevent saving if form is not valid?
    const useOwnDomain = this.senderInfoForm.get('useOwn').value;
    const info: SaveData = {
      domain: useOwnDomain ? this.selectedDomain.domain : DEFAULT_SENDER_DOMAIN,
      preferredDisplayName: this.senderInfoForm.get('senderName').value,
      preferredEmailUsername: this.senderInfoForm.get('senderEmailUsername').value,
      senderName: this.senderInfoForm.get('senderName').value,
      replyTo: this.senderInfoForm.get('replyTo').value,
    };
    return info;
  }

  async openChooseDomainDialog(): Promise<void> {
    const dialog = this.dialog.open(DomainsDialogComponent, {
      data: this.sender,
      maxHeight: '600px',
    });
    const result: DialogResult = await oneResultFrom(dialog.afterClosed());
    switch (result.choiceType) {
      case 'new':
        this.authorizeRequested.emit();
        break;
      case 'valid':
        this.domainChangeRequested.emit(result.newDomain);
        break;
      case 'invalid':
        this.invalidDomainChangeRequested.emit(result.newDomain);
        break;
    }
  }
}
